import { useState, useEffect, useMemo } from "react";
import apiClient from "@/utils/apiClient";
import downloadFile from "@/utils/fileDownloader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Activity as ActivityIcon,
  Plus,
  Download,
  Upload,
  Calendar,
  Clock,
  Filter,
  Search,
  RefreshCw,
  Copy,
  CheckCircle,
  User,
  Users,
  Trophy,
  Palette,
  GraduationCap,
  Sparkles,
  Info,
  FileText,
  LayoutGrid,
  List as ListIcon,
  Edit,
  Trash2,
  CheckSquare,
  Square,
  AlertTriangle,
  X,
  Check
} from "lucide-react";

const Activities = ({ permissions = {} }) => {
  const [students, setStudents] = useState([]);
  const [activitiesList, setActivitiesList] = useState([]);
  const [groupedActivities, setGroupedActivities] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Bulk Upload states
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkStartDate, setBulkStartDate] = useState("");
  const [bulkEndDate, setBulkEndDate] = useState("");

  // Confirmation for duplicates
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  // Copy tracking banner in modal
  const [copiedFromInfo, setCopiedFromInfo] = useState(null);

  // View switch: 'feed' (like admin activity log) or 'grouped' (grouped by student)
  const [viewMode, setViewMode] = useState("feed");

  // Filters matching admin activity log
  const [filters, setFilters] = useState({
    student_id: "all",
    activity_type: "all",
    search: ""
  });

  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    student_id: "",
    date: today,
    activity_type: "sports",
    activity_name: "",
    description: "",
    remarks: ""
  });

  // Bulk Add Activity States (Date range & multiple assigned students)
  const [showBulkAddDialog, setShowBulkAddDialog] = useState(false);
  const [bulkAddStartDate, setBulkAddStartDate] = useState(today);
  const [bulkAddEndDate, setBulkAddEndDate] = useState(today);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [bulkAddFormData, setBulkAddFormData] = useState({
    activity_type: "sports",
    activity_name: "",
    description: "",
    remarks: "",
    overwrite_existing: true
  });
  const [bulkStudentSearch, setBulkStudentSearch] = useState("");
  const [bulkStudentClassFilter, setBulkStudentClassFilter] = useState("all");
  const [bulkAddLoading, setBulkAddLoading] = useState(false);

  // Edit Activity States
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: "",
    student_id: "",
    student_name: "",
    readable_id: "",
    date: today,
    activity_type: "sports",
    activity_name: "",
    description: "",
    remarks: ""
  });

  // Delete Activity States
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Permission check: if permissions object explicitly has can_update_activities as false, restrict. Otherwise allow.
  const canUpdate = permissions?.can_update_activities !== false;

  useEffect(() => {
    fetchStudents();
    fetchActivities();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await apiClient.get("/v1/warden/students/my-students");
      const list = response.data.students || [];
      setStudents(list);
    } catch (error) {
      console.error("Failed to load students:", error);
    }
  };

  const fetchActivities = async () => {
    setFetching(true);
    try {
      const response = await apiClient.get("/v1/warden/activities/recent");
      const data = response.data || {};
      
      let rawFlat = [];
      let rawGrouped = [];

      if (data.activities && Array.isArray(data.activities)) {
        rawFlat = data.activities;
        rawGrouped = data.grouped || [];
      } else if (Array.isArray(data)) {
        // Legacy grouped format fallback
        rawGrouped = data;
        data.forEach(group => {
          (group.activities || []).forEach(act => {
            rawFlat.push({
              ...act,
              student_name: group.student_name,
              readable_id: group.readable_id
            });
          });
        });
      }

      setActivitiesList(rawFlat);
      setGroupedActivities(rawGrouped);
    } catch (error) {
      console.error("Failed to load activities:", error);
      toast.error("Failed to load activities");
    } finally {
      setFetching(false);
    }
  };

  const handleRefresh = () => {
    fetchActivities();
    fetchStudents();
    toast.success("Activities refreshed");
  };

  // Helper colors and icons matching activity types
  const getActivityTypeConfig = (type) => {
    const t = (type || "").toLowerCase().trim();
    if (t === "sports" || t === "cricket" || t === "football") {
      return {
        label: "Sports",
        icon: <Trophy className="w-4 h-4 text-emerald-600" />,
        color: "bg-emerald-50 text-emerald-700 border-emerald-200",
        badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
        ring: "hover:border-emerald-300"
      };
    }
    if (t === "cultural" || t === "music" || t === "dance" || t === "art" || t === "creative") {
      return {
        label: "Cultural",
        icon: <Palette className="w-4 h-4 text-purple-600" />,
        color: "bg-purple-50 text-purple-700 border-purple-200",
        badge: "bg-purple-100 text-purple-800 border-purple-300",
        ring: "hover:border-purple-300"
      };
    }
    if (t === "academic" || t === "study" || t === "quiz") {
      return {
        label: "Academic",
        icon: <GraduationCap className="w-4 h-4 text-blue-600" />,
        color: "bg-blue-50 text-blue-700 border-blue-200",
        badge: "bg-blue-100 text-blue-800 border-blue-300",
        ring: "hover:border-blue-300"
      };
    }
    return {
      label: type ? type.charAt(0).toUpperCase() + type.slice(1) : "Other",
      icon: <Sparkles className="w-4 h-4 text-amber-600" />,
      color: "bg-amber-50 text-amber-700 border-amber-200",
      badge: "bg-amber-100 text-amber-800 border-amber-300",
      ring: "hover:border-amber-300"
    };
  };

  // Format relative timestamp / readable date
  const formatTimestamp = (timestamp, recordDate) => {
    if (timestamp) {
      try {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;

        if (diff < 3600000) {
          const mins = Math.max(1, Math.floor(diff / 60000));
          return mins <= 1 ? "Just now" : `${mins} mins ago`;
        }
        if (diff < 86400000) {
          const hours = Math.floor(diff / 3600000);
          return `${hours} hr${hours > 1 ? "s" : ""} ago`;
        }
        if (diff < 604800000) {
          const days = Math.floor(diff / 86400000);
          return `${days} day${days > 1 ? "s" : ""} ago`;
        }
      } catch (e) {
        // fallback
      }
    }
    return recordDate || "Recent";
  };

  // Computed Stats
  const stats = useMemo(() => {
    const total = activitiesList.length;
    let sportsCount = 0;
    let culturalCount = 0;
    let academicCount = 0;
    let otherCount = 0;
    const studentSet = new Set();

    activitiesList.forEach(act => {
      if (act.student_id) studentSet.add(act.student_id);
      const t = (act.activity_type || "").toLowerCase().trim();
      if (t === "sports" || t === "cricket" || t === "football") {
        sportsCount++;
      } else if (t === "cultural" || t === "music" || t === "dance" || t === "art" || t === "creative") {
        culturalCount++;
      } else if (t === "academic" || t === "study" || t === "quiz") {
        academicCount++;
      } else {
        otherCount++;
      }
    });

    return {
      total,
      sports: sportsCount,
      cultural: culturalCount,
      academic: academicCount,
      other: otherCount,
      studentsCount: studentSet.size
    };
  }, [activitiesList]);

  // Filtered Activities
  const filteredActivities = useMemo(() => {
    return activitiesList.filter(act => {
      // Student filter
      if (filters.student_id !== "all" && act.student_id !== filters.student_id) {
        return false;
      }
      // Activity type filter
      if (filters.activity_type !== "all") {
        const actType = (act.activity_type || "").toLowerCase().trim();
        const filterType = filters.activity_type.toLowerCase();
        if (filterType === "sports" && !["sports", "cricket", "football"].includes(actType)) return false;
        if (filterType === "cultural" && !["cultural", "music", "dance", "art", "creative"].includes(actType)) return false;
        if (filterType === "academic" && !["academic", "study", "quiz"].includes(actType)) return false;
        if (filterType === "other" && ["sports", "cultural", "academic", "cricket", "football", "music", "dance", "art", "creative", "study", "quiz"].includes(actType)) return false;
      }
      // Search filter
      if (filters.search.trim()) {
        const q = filters.search.toLowerCase();
        const nameMatch = (act.activity_name || "").toLowerCase().includes(q);
        const descMatch = (act.description || "").toLowerCase().includes(q);
        const remarksMatch = (act.remarks || "").toLowerCase().includes(q);
        const studentMatch = (act.student_name || "").toLowerCase().includes(q);
        const idMatch = (act.readable_id || "").toLowerCase().includes(q);
        if (!nameMatch && !descMatch && !remarksMatch && !studentMatch && !idMatch) {
          return false;
        }
      }
      return true;
    });
  }, [activitiesList, filters]);

  // Filtered Grouped Activities
  const filteredGroupedActivities = useMemo(() => {
    return groupedActivities.map(group => {
      const filteredGroupActs = (group.activities || []).filter(act => {
        if (filters.activity_type !== "all") {
          const actType = (act.activity_type || "").toLowerCase().trim();
          const filterType = filters.activity_type.toLowerCase();
          if (filterType === "sports" && !["sports", "cricket", "football"].includes(actType)) return false;
          if (filterType === "cultural" && !["cultural", "music", "dance", "art", "creative"].includes(actType)) return false;
          if (filterType === "academic" && !["academic", "study", "quiz"].includes(actType)) return false;
          if (filterType === "other" && ["sports", "cultural", "academic", "cricket", "football", "music", "dance", "art", "creative", "study", "quiz"].includes(actType)) return false;
        }
        if (filters.search.trim()) {
          const q = filters.search.toLowerCase();
          const nameMatch = (act.activity_name || "").toLowerCase().includes(q);
          const descMatch = (act.description || "").toLowerCase().includes(q);
          const remarksMatch = (act.remarks || "").toLowerCase().includes(q);
          const studentMatch = (group.student_name || "").toLowerCase().includes(q);
          const idMatch = (group.readable_id || "").toLowerCase().includes(q);
          if (!nameMatch && !descMatch && !remarksMatch && !studentMatch && !idMatch) {
            return false;
          }
        }
        return true;
      });

      return {
        ...group,
        activities: filteredGroupActs
      };
    }).filter(group => {
      if (filters.student_id !== "all" && group.student_id !== filters.student_id) return false;
      return group.activities.length > 0;
    });
  }, [groupedActivities, filters]);

  const clearFilters = () => {
    setFilters({
      student_id: "all",
      activity_type: "all",
      search: ""
    });
  };

  const hasActiveFilters = filters.student_id !== "all" || filters.activity_type !== "all" || filters.search.trim() !== "";

  // -------------------------------------------------------------
  // COPY ACTIVITY HANDLERS
  // -------------------------------------------------------------
  const handleCopyActivity = (activity) => {
    setFormData({
      student_id: activity.student_id || (students[0]?.id || ""),
      date: today,
      activity_type: (activity.activity_type || "sports").toLowerCase(),
      activity_name: activity.activity_name || "",
      description: activity.description || "",
      remarks: activity.remarks || ""
    });

    setCopiedFromInfo({
      activity_name: activity.activity_name || "Activity",
      student_name: activity.student_name || "Student",
      type: activity.activity_type || "sports"
    });

    setShowAddDialog(true);
    toast.success(`Copied "${activity.activity_name || 'Activity'}" details to form`);
  };

  const handleCopyLastRecord = () => {
    if (activitiesList.length === 0) {
      toast.error("No previous activities found to copy from");
      return;
    }

    const last = activitiesList[0];
    setFormData({
      student_id: last.student_id || (students[0]?.id || ""),
      date: today,
      activity_type: (last.activity_type || "sports").toLowerCase(),
      activity_name: last.activity_name || "",
      description: last.description || "",
      remarks: last.remarks || ""
    });

    setCopiedFromInfo({
      activity_name: last.activity_name || "Activity",
      student_name: last.student_name || "Last Record",
      type: last.activity_type || "sports"
    });

    setShowAddDialog(true);
    toast.success(`Auto-filled from latest record (${last.activity_name || 'Activity'})`);
  };

  const handleOpenNewModal = () => {
    setCopiedFromInfo(null);
    setFormData({
      student_id: students[0]?.id || "",
      date: today,
      activity_type: "sports",
      activity_name: "",
      description: "",
      remarks: ""
    });
    setShowAddDialog(true);
  };

  // -------------------------------------------------------------
  // BULK ADD ACTIVITY HELPERS & HANDLERS
  // -------------------------------------------------------------
  const handleDateShortcut = (shortcut) => {
    const now = new Date();
    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    if (shortcut === "today") {
      const d = formatDate(now);
      setBulkAddStartDate(d);
      setBulkAddEndDate(d);
    } else if (shortcut === "yesterday") {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const d = formatDate(yesterday);
      setBulkAddStartDate(d);
      setBulkAddEndDate(d);
    } else if (shortcut === "last3days") {
      const past = new Date(now);
      past.setDate(past.getDate() - 2);
      setBulkAddStartDate(formatDate(past));
      setBulkAddEndDate(formatDate(now));
    } else if (shortcut === "last7days") {
      const past = new Date(now);
      past.setDate(past.getDate() - 6);
      setBulkAddStartDate(formatDate(past));
      setBulkAddEndDate(formatDate(now));
    }
  };

  const bulkDaysCount = useMemo(() => {
    if (!bulkAddStartDate || !bulkAddEndDate) return 0;
    try {
      const start = new Date(bulkAddStartDate);
      const end = new Date(bulkAddEndDate);
      if (end < start) return 0;
      const diffTime = Math.abs(end - start);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    } catch {
      return 0;
    }
  }, [bulkAddStartDate, bulkAddEndDate]);

  const availableClasses = useMemo(() => {
    const set = new Set();
    students.forEach(s => {
      if (s.class_name) set.add(s.class_name);
    });
    return Array.from(set).sort();
  }, [students]);

  const filteredAssignedStudents = useMemo(() => {
    return students.filter(s => {
      if (bulkStudentClassFilter !== "all" && s.class_name !== bulkStudentClassFilter) {
        return false;
      }
      if (bulkStudentSearch.trim()) {
        const q = bulkStudentSearch.toLowerCase();
        const nameMatch = (s.name || "").toLowerCase().includes(q);
        const idMatch = (s.readable_id || s.id || "").toLowerCase().includes(q);
        const roomMatch = (s.room_number || "").toLowerCase().includes(q);
        const classMatch = (s.class_name || "").toLowerCase().includes(q);
        return nameMatch || idMatch || roomMatch || classMatch;
      }
      return true;
    });
  }, [students, bulkStudentSearch, bulkStudentClassFilter]);

  const handleToggleStudent = (studentId) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAllStudents = () => {
    const idsToSelect = filteredAssignedStudents.map(s => s.id);
    setSelectedStudentIds(prev => Array.from(new Set([...prev, ...idsToSelect])));
  };

  const handleDeselectAllStudents = () => {
    if (bulkStudentSearch.trim() || bulkStudentClassFilter !== "all") {
      const idsToRemove = new Set(filteredAssignedStudents.map(s => s.id));
      setSelectedStudentIds(prev => prev.filter(id => !idsToRemove.has(id)));
    } else {
      setSelectedStudentIds([]);
    }
  };

  const handleBulkAddSubmit = async (e) => {
    e.preventDefault();
    if (selectedStudentIds.length === 0) {
      toast.error("Please select at least one assigned student");
      return;
    }
    if (!bulkAddStartDate || !bulkAddEndDate) {
      toast.error("Please select start and end dates");
      return;
    }
    if (new Date(bulkAddStartDate) > new Date(bulkAddEndDate)) {
      toast.error("Start date cannot be after end date");
      return;
    }
    if (!bulkAddFormData.activity_name.trim()) {
      toast.error("Please enter an activity name");
      return;
    }

    setBulkAddLoading(true);
    try {
      const payload = {
        student_ids: selectedStudentIds,
        start_date: bulkAddStartDate,
        end_date: bulkAddEndDate,
        activity_type: bulkAddFormData.activity_type,
        activity_name: bulkAddFormData.activity_name.trim(),
        description: bulkAddFormData.description || "",
        remarks: bulkAddFormData.remarks || "",
        overwrite_existing: bulkAddFormData.overwrite_existing
      };

      const response = await apiClient.post("/v1/warden/activities/bulk-add", payload);
      
      toast.success(
        response.data?.message || 
        `Successfully logged activities for ${selectedStudentIds.length} student(s)!`
      );

      setShowBulkAddDialog(false);
      setSelectedStudentIds([]);
      setBulkAddFormData({
        activity_type: "sports",
        activity_name: "",
        description: "",
        remarks: "",
        overwrite_existing: true
      });
      fetchActivities();
    } catch (error) {
      console.error("Bulk add failed:", error);
      toast.error(error.response?.data?.detail || "Failed to bulk add activities");
    } finally {
      setBulkAddLoading(false);
    }
  };

  // -------------------------------------------------------------
  // EDIT ACTIVITY HANDLERS
  // -------------------------------------------------------------
  const handleOpenEditModal = (activity) => {
    setEditFormData({
      id: activity.id,
      student_id: activity.student_id || "",
      student_name: activity.student_name || "Student",
      readable_id: activity.readable_id || "",
      date: activity.date || today,
      activity_type: (activity.activity_type || "sports").toLowerCase(),
      activity_name: activity.activity_name || "",
      description: activity.description || "",
      remarks: activity.remarks || ""
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData.activity_name.trim()) {
      toast.error("Please enter an activity name");
      return;
    }

    setEditLoading(true);
    try {
      await apiClient.put(`/v1/warden/activities/${editFormData.id}`, {
        student_id: editFormData.student_id,
        date: editFormData.date,
        activity_type: editFormData.activity_type,
        activity_name: editFormData.activity_name.trim(),
        description: editFormData.description || "",
        remarks: editFormData.remarks || ""
      });

      toast.success("Activity updated successfully");
      setShowEditDialog(false);
      fetchActivities();
    } catch (error) {
      console.error("Failed to update activity:", error);
      toast.error(error.response?.data?.detail || "Failed to update activity");
    } finally {
      setEditLoading(false);
    }
  };

  // -------------------------------------------------------------
  // DELETE ACTIVITY HANDLERS
  // -------------------------------------------------------------
  const handleOpenDeleteDialog = (activity) => {
    setActivityToDelete(activity);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!activityToDelete?.id) return;

    setDeleteLoading(true);
    try {
      await apiClient.delete(`/v1/warden/activities/${activityToDelete.id}`);
      toast.success("Activity deleted successfully");
      setShowDeleteDialog(false);
      setActivityToDelete(null);
      fetchActivities();
    } catch (error) {
      console.error("Failed to delete activity:", error);
      toast.error(error.response?.data?.detail || "Failed to delete activity");
    } finally {
      setDeleteLoading(false);
    }
  };

  // -------------------------------------------------------------
  // SUBMIT HANDLERS
  // -------------------------------------------------------------
  const handleAdd = async (e, forceUpdate = false) => {
    e.preventDefault();
    if (!formData.student_id) {
      toast.error("Please select a student");
      return;
    }
    if (!formData.activity_name.trim()) {
      toast.error("Please enter an activity name");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        force_update: forceUpdate
      };

      const response = await apiClient.post("/v1/warden/activities/add", payload);

      if (response.data.error) {
        toast.error(response.data.error);
        return;
      }

      // Check if duplicate found
      if (response.data.duplicate_found && !forceUpdate) {
        setShowAddDialog(false);
        setDuplicateInfo({
          existing: response.data.existing_record,
          message: response.data.message
        });
        setTimeout(() => {
          setShowConfirmDialog(true);
        }, 100);
        return;
      }

      toast.success(forceUpdate ? "Activity updated successfully" : "Activity added successfully");
      setShowAddDialog(false);
      setShowConfirmDialog(false);
      setDuplicateInfo(null);
      setCopiedFromInfo(null);
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save activity");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOverwrite = async (e) => {
    e.preventDefault();
    setShowConfirmDialog(false);
    handleAdd(e, true);
  };

  // -------------------------------------------------------------
  // BULK TEMPLATE & UPLOAD HANDLERS
  // -------------------------------------------------------------
  const handleBulkDownload = async () => {
    if (!bulkStartDate || !bulkEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    try {
      const response = await apiClient.get("/v1/warden/activities/template", {
        params: {
          start_date: bulkStartDate,
          end_date: bulkEndDate
        },
        responseType: "blob"
      });

      const fileName = `activities_template_${bulkStartDate}_to_${bulkEndDate}.xlsx`;
      const mimeType = response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      await downloadFile(response.data, fileName, mimeType);
    } catch (error) {
      toast.error("Failed to download activities template");
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error("Please select a file to upload");
      return;
    }

    const uploadData = new FormData();
    uploadData.append("file", bulkFile);

    setLoading(true);
    try {
      const response = await apiClient.post("/v1/warden/activities/bulk-upload", uploadData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success(
        `Upload complete: ${response.data.successful || 0} successful, ${response.data.failed || 0} failed`
      );
      setBulkFile(null);
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload activities file");
    } finally {
      setLoading(false);
    }
  };

  if (!canUpdate) {
    return (
      <div className="p-6">
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="p-8 text-center">
            <ActivityIcon className="w-12 h-12 mx-auto text-amber-500 mb-3" />
            <h3 className="text-lg font-bold text-gray-800">Permission Required</h3>
            <p className="text-sm text-gray-600 mt-1">
              You do not currently have permission to record or manage student activities.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Please contact your hostel administrator to enable activity management access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* ------------------------------------------------------------- */}
      {/* PAGE HEADER */}
      {/* ------------------------------------------------------------- */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-gray-100">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-blue-50 border border-blue-100">
              <ActivityIcon className="w-6 h-6 text-[#2C5F7F]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: "#2C5F7F" }}>
                Activities Management
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                Monitor, record, and manage student extracurricular and academic activities
              </p>
            </div>
          </div>
        </div>

        {/* Action Button Cluster */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <Button
            onClick={handleRefresh}
            variant="outline"
            size="sm"
            className="flex items-center gap-2 text-gray-700 hover:bg-gray-100"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${fetching ? "animate-spin text-blue-600" : ""}`} />
            Refresh
          </Button>

          <Button
            onClick={() => {
              setBulkAddStartDate(today);
              setBulkAddEndDate(today);
              setSelectedStudentIds([]);
              setBulkStudentSearch("");
              setBulkStudentClassFilter("all");
              setBulkAddFormData({
                activity_type: "sports",
                activity_name: "",
                description: "",
                remarks: "",
                overwrite_existing: true
              });
              setShowBulkAddDialog(true);
            }}
            variant="outline"
            size="sm"
            className="flex items-center gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-medium"
            title="Bulk record an activity for multiple assigned students across a date range"
          >
            <Users className="w-3.5 h-3.5 text-indigo-600" />
            <span>Bulk Add Activity</span>
          </Button>


          <Button
            onClick={handleOpenNewModal}
            size="sm"
            className="btn-primary text-white flex items-center gap-2 shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Activity
          </Button>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* STATS OVERVIEW CARDS (Matching Admin Activity Log) */}
      {/* ------------------------------------------------------------- */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {/* Total Activities */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 border border-blue-200">
              <ActivityIcon className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>
              {stats.total}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Total Activities</p>
          </CardContent>
        </Card>

        {/* Sports */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center bg-emerald-100 text-emerald-700 border border-emerald-200">
              <Trophy className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-emerald-800">
              {stats.sports}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Sports</p>
          </CardContent>
        </Card>

        {/* Cultural */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center bg-purple-100 text-purple-700 border border-purple-200">
              <Palette className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-purple-800">
              {stats.cultural}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Cultural</p>
          </CardContent>
        </Card>

        {/* Academic */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center bg-blue-100 text-blue-700 border border-blue-200">
              <GraduationCap className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-blue-800">
              {stats.academic}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Academic</p>
          </CardContent>
        </Card>

        {/* Other */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center bg-amber-100 text-amber-700 border border-amber-200">
              <Sparkles className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-amber-800">
              {stats.other}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Other</p>
          </CardContent>
        </Card>

        {/* Participating Students */}
        <Card className="hover:shadow-sm transition-shadow">
          <CardContent className="p-4 text-center">
            <div className="w-8 h-8 mx-auto mb-2 rounded-full flex items-center justify-center bg-indigo-100 text-indigo-700 border border-indigo-200">
              <Users className="w-4 h-4" />
            </div>
            <p className="text-2xl font-bold text-indigo-800">
              {stats.studentsCount}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Students</p>
          </CardContent>
        </Card>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* TABS: RECENT ACTIVITIES VS BULK UPLOAD */}
      {/* ------------------------------------------------------------- */}
      <Tabs defaultValue="activities" className="space-y-4">
        <TabsList className="grid w-full sm:w-80 grid-cols-2">
          <TabsTrigger value="activities" className="flex items-center gap-1.5">
            <ActivityIcon className="w-3.5 h-3.5" />
            <span>Activities Log</span>
          </TabsTrigger>
          <TabsTrigger value="bulk" className="flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" />
            <span>Bulk Upload</span>
          </TabsTrigger>
        </TabsList>

        {/* ========================================================= */}
        {/* TAB 1: ACTIVITIES LOG (FEED & GROUPED) */}
        {/* ========================================================= */}
        <TabsContent value="activities" className="space-y-4">
          {/* Filters Bar Card (Matching Admin Activity Log) */}
          <Card className="border-gray-200/80 shadow-xs">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 mr-1">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <span className="text-xs sm:text-sm font-semibold text-gray-700">Filters:</span>
                </div>

                {/* Filter by Student */}
                <div className="w-48 sm:w-56">
                  <Select
                    value={filters.student_id}
                    onValueChange={(val) => setFilters(prev => ({ ...prev, student_id: val }))}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="All Students" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Students ({students.length})</SelectItem>
                      {students.map(s => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} ({s.readable_id || s.class_name || "Student"})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Filter by Activity Type */}
                <div className="w-36 sm:w-44">
                  <Select
                    value={filters.activity_type}
                    onValueChange={(val) => setFilters(prev => ({ ...prev, activity_type: val }))}
                  >
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="sports">Sports</SelectItem>
                      <SelectItem value="cultural">Cultural</SelectItem>
                      <SelectItem value="academic">Academic</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Search Text Input */}
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search by name, description, notes..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="h-9 pl-9 text-xs"
                  />
                </div>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-xs text-rose-600 hover:bg-rose-50 h-9"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Activities List Card */}
          <Card className="border-gray-200/80 shadow-xs">
            <CardHeader className="pb-3 border-b border-gray-100 flex flex-row items-center justify-between">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2 text-gray-900 font-bold">
                <ActivityIcon className="w-5 h-5 text-blue-600" />
                <span>Recent Activities</span>
                <span className="text-xs font-normal text-gray-500">
                  ({filteredActivities.length} {filteredActivities.length === 1 ? "entry" : "entries"})
                </span>
              </CardTitle>

              {/* View Switcher: Log Feed vs Grouped */}
              <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                <button
                  type="button"
                  onClick={() => setViewMode("feed")}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${
                    viewMode === "feed"
                      ? "bg-white text-gray-900 shadow-xs"
                      : "text-gray-500 hover:text-gray-900"
                  }`}
                  title="Feed layout like Admin Activity Log"
                >
                  <ListIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Feed</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("grouped")}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${
                    viewMode === "grouped"
                      ? "bg-white text-gray-900 shadow-xs"
                      : "text-gray-500 hover:text-gray-900"
                  }`}
                  title="Grouped by student"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Grouped</span>
                </button>
              </div>
            </CardHeader>

            <CardContent className="pt-4">
              {fetching ? (
                <div className="py-12 text-center text-gray-500 text-sm flex flex-col items-center gap-2">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                  <span>Loading activities...</span>
                </div>
              ) : filteredActivities.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <ActivityIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-700">No activities found</p>
                  {hasActiveFilters ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Try adjusting your filters or search query.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Get started by adding an activity or copying from a template.
                    </p>
                  )}
                  <div className="mt-4 flex justify-center gap-2">
                    {hasActiveFilters && (
                      <Button variant="outline" size="sm" onClick={clearFilters} className="text-xs">
                        Clear Filters
                      </Button>
                    )}
                    <Button size="sm" onClick={handleOpenNewModal} className="btn-primary text-white text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add First Activity
                    </Button>
                  </div>
                </div>
              ) : viewMode === "feed" ? (
                /* ========================================================= */
                /* VIEW 1: ACTIVITY LOG FEED (MATCHING ADMIN ACTIVITY LOG) */
                /* ========================================================= */
                <div className="space-y-3">
                  {filteredActivities.map((activity, idx) => {
                    const typeConfig = getActivityTypeConfig(activity.activity_type);

                    return (
                      <div
                        key={activity.id || idx}
                        className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 p-4 bg-gray-50/70 hover:bg-blue-50/20 rounded-xl border border-gray-100 hover:border-blue-200 transition-all duration-200"
                      >
                        <div className="flex items-start gap-3.5 flex-1 min-w-0">
                          {/* Module / Type Icon */}
                          <div
                            className={`p-2.5 rounded-xl border shrink-0 mt-0.5 ${typeConfig.color}`}
                            title={typeConfig.label}
                          >
                            {typeConfig.icon}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-gray-900 text-sm">
                                {activity.student_name}
                              </span>
                              <span className="text-gray-300">•</span>
                              <span className="font-semibold text-gray-800 text-sm">
                                {activity.activity_name}
                              </span>
                              {activity.readable_id && (
                                <Badge variant="outline" className="text-[10px] font-mono py-0 px-1.5 text-gray-500 bg-white">
                                  {activity.readable_id}
                                </Badge>
                              )}
                              {activity.class_name && (
                                <span className="text-xs text-gray-500">
                                  Class {activity.class_name}
                                </span>
                              )}
                            </div>

                            {/* Description */}
                            {activity.description && (
                              <p className="text-xs text-gray-600 leading-relaxed pt-0.5">
                                {activity.description}
                              </p>
                            )}

                            {/* Remarks */}
                            {activity.remarks && (
                              <div className="flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50/70 border border-amber-200/60 px-2 py-0.5 rounded-md w-fit mt-1">
                                <Info className="w-3 h-3 text-amber-600 shrink-0" />
                                <span>Note: {activity.remarks}</span>
                              </div>
                            )}

                            {/* Timestamp & Date */}
                            <div className="flex items-center gap-4 text-[11px] text-gray-400 pt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-gray-400" />
                                {activity.date}
                              </span>
                              {activity.created_at && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3 text-gray-400" />
                                  {formatTimestamp(activity.created_at, activity.date)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right: Badge & Actions (Copy, Edit, Delete) */}
                        <div className="flex items-center justify-between sm:justify-end gap-1.5 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100 flex-wrap">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${typeConfig.badge}`}
                          >
                            {activity.activity_type}
                          </span>

                          <div className="flex items-center gap-1">
                            <Button
                              onClick={() => handleCopyActivity(activity)}
                              variant="outline"
                              size="sm"
                              className="h-8 px-2.5 text-xs flex items-center gap-1 border-blue-200 text-blue-700 hover:bg-blue-50 hover:border-blue-300 shadow-2xs"
                              title="Copy activity options and details into a new record"
                            >
                              <Copy className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Copy</span>
                            </Button>

                            <Button
                              onClick={() => handleOpenEditModal(activity)}
                              variant="outline"
                              size="sm"
                              className="h-8 px-2.5 text-xs flex items-center gap-1 border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300 shadow-2xs"
                              title="Edit this activity record"
                            >
                              <Edit className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Edit</span>
                            </Button>

                            <Button
                              onClick={() => handleOpenDeleteDialog(activity)}
                              variant="outline"
                              size="sm"
                              className="h-8 px-2 text-xs flex items-center gap-1 border-rose-200 text-rose-600 hover:bg-rose-50 hover:border-rose-300 shadow-2xs"
                              title="Delete this activity record"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span className="hidden sm:inline">Delete</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* ========================================================= */
                /* VIEW 2: GROUPED BY STUDENT VIEW */
                /* ========================================================= */
                <div className="space-y-8">
                  {filteredGroupedActivities.map(studentGroup => (
                    <div key={studentGroup.student_id} className="space-y-3">
                      {/* Student Header */}
                      <div className="flex items-center gap-3 pb-2 border-b border-gray-200">
                        <div className="p-1.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-gray-900">
                            {studentGroup.student_name}
                          </h3>
                          <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                            ID: {studentGroup.readable_id || "N/A"} {studentGroup.class_name ? `• Class ${studentGroup.class_name}` : ""}
                          </span>
                        </div>
                        <div className="ml-auto">
                          <Badge variant="secondary" className="text-xs">
                            {studentGroup.activities?.length || 0} Entries
                          </Badge>
                        </div>
                      </div>

                      {/* Student Activity Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {studentGroup.activities.map(activity => {
                          const typeConfig = getActivityTypeConfig(activity.activity_type);

                          return (
                            <div
                              key={activity.id}
                              className="p-3.5 rounded-xl border border-gray-100 hover:border-blue-200 hover:bg-blue-50/20 transition-all duration-200 shadow-2xs space-y-2.5"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className={`p-1.5 rounded-lg border shrink-0 ${typeConfig.color}`}>
                                    {typeConfig.icon}
                                  </div>
                                  <span className="text-sm font-semibold text-gray-800 truncate">
                                    {activity.activity_name}
                                  </span>
                                </div>
                                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0 ${typeConfig.badge}`}>
                                  {activity.activity_type}
                                </span>
                              </div>

                              <div className="space-y-1.5 text-xs">
                                <div className="flex items-center gap-1.5 text-gray-400">
                                  <Calendar className="w-3 h-3" />
                                  <span>{activity.date}</span>
                                </div>

                                {activity.description && (
                                  <p className="text-gray-600 line-clamp-2">
                                    {activity.description}
                                  </p>
                                )}

                                {activity.remarks && (
                                  <p className="text-[11px] text-amber-700 italic bg-amber-50/60 px-2 py-0.5 rounded border border-amber-100/80">
                                    Note: {activity.remarks}
                                  </p>
                                )}
                              </div>

                              <div className="flex justify-end items-center gap-1 pt-1.5 border-t border-gray-100 flex-wrap">
                                <Button
                                  onClick={() => handleCopyActivity({ ...activity, student_name: studentGroup.student_name, readable_id: studentGroup.readable_id })}
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-blue-700 hover:bg-blue-50 gap-1 px-2"
                                  title="Copy this activity"
                                >
                                  <Copy className="w-3 h-3" />
                                  <span>Copy</span>
                                </Button>
                                <Button
                                  onClick={() => handleOpenEditModal({ ...activity, student_name: studentGroup.student_name, readable_id: studentGroup.readable_id })}
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-amber-700 hover:bg-amber-50 gap-1 px-2"
                                  title="Edit this activity"
                                >
                                  <Edit className="w-3 h-3" />
                                  <span>Edit</span>
                                </Button>
                                <Button
                                  onClick={() => handleOpenDeleteDialog({ ...activity, student_name: studentGroup.student_name, readable_id: studentGroup.readable_id })}
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs text-rose-600 hover:bg-rose-50 gap-1 px-2"
                                  title="Delete this activity"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  <span>Delete</span>
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========================================================= */}
        {/* TAB 2: BULK UPLOAD ACTIVITIES */}
        {/* ========================================================= */}
        <TabsContent value="bulk">
          <Card className="border-gray-200/80 shadow-xs">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-600" />
                Bulk Upload Activities
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5 pt-5">
              <div className="bg-blue-50/60 border border-blue-200 p-4 rounded-xl text-xs text-blue-900 space-y-1.5">
                <h4 className="font-bold flex items-center gap-1.5 text-sm text-blue-950">
                  <Info className="w-4 h-4 text-blue-600" />
                  Instructions for Bulk Upload
                </h4>
                <ol className="list-decimal ml-4 space-y-1 text-slate-700">
                  <li>Pick a start date and end date for which you wish to log activities.</li>
                  <li>Click <strong>Download Template</strong> to generate an Excel sheet pre-populated with your students.</li>
                  <li>Fill in activity names in the respective date columns.</li>
                  <li>Upload the updated spreadsheet to record all activities in a single step.</li>
                </ol>
              </div>

              {/* Date Range Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-gray-700 mb-1 block">Start Date *</Label>
                  <Input
                    type="date"
                    max={today}
                    value={bulkStartDate}
                    onChange={(e) => setBulkStartDate(e.target.value)}
                    className="text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-700 mb-1 block">End Date *</Label>
                  <Input
                    type="date"
                    max={today}
                    min={bulkStartDate || ""}
                    value={bulkEndDate}
                    onChange={(e) => setBulkEndDate(e.target.value)}
                    className="text-xs"
                  />
                </div>
              </div>

              {bulkStartDate && bulkEndDate && (
                <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200">
                  📅 Template spans{" "}
                  <strong>
                    {Math.max(1, Math.ceil((new Date(bulkEndDate) - new Date(bulkStartDate)) / (1000 * 60 * 60 * 24)) + 1)} day(s)
                  </strong>{" "}
                  across <strong>{students.length} students</strong>.
                </div>
              )}

              <Button
                onClick={handleBulkDownload}
                variant="outline"
                className="w-full text-xs flex items-center justify-center gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
                disabled={!bulkStartDate || !bulkEndDate}
              >
                <Download className="w-4 h-4" />
                Download Template {bulkStartDate && bulkEndDate ? `(${bulkStartDate} to ${bulkEndDate})` : ""}
              </Button>

              {/* File Dropzone */}
              <div className="border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors rounded-xl p-6 text-center bg-gray-50/50">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="activities-bulk-file"
                />
                <label htmlFor="activities-bulk-file" className="cursor-pointer block space-y-2">
                  <Upload className="w-10 h-10 mx-auto text-gray-400" />
                  <p className="text-sm font-semibold text-gray-700">
                    {bulkFile ? bulkFile.name : "Click to select completed Excel file"}
                  </p>
                  <p className="text-xs text-gray-400">Supported formats: .xlsx, .xls</p>
                </label>
              </div>

              {bulkFile && (
                <Button
                  onClick={handleBulkUpload}
                  disabled={loading}
                  className="w-full btn-primary text-white text-xs flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Uploading Activities...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload & Process Activities
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ------------------------------------------------------------- */}
      {/* ADD / COPY ACTIVITY DIALOG */}
      {/* ------------------------------------------------------------- */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <ActivityIcon className="w-5 h-5 text-blue-600" />
              {copiedFromInfo ? "Add Activity (from Copied Details)" : "Add New Activity"}
            </DialogTitle>
          </DialogHeader>

          {/* Quick Auto-Fill / Copied banner */}
          {copiedFromInfo ? (
            <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 flex items-start gap-2">
              <Copy className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold">Auto-filled from:</span>{" "}
                <span className="font-semibold">{copiedFromInfo.activity_name}</span> ({copiedFromInfo.student_name}).
                <p className="text-slate-600 mt-0.5 text-[11px]">
                  All options are pre-selected. You can switch the student or tweak any fields as desired.
                </p>
              </div>
            </div>
          ) : activitiesList.length > 0 ? (
            <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-xl border border-gray-200">
              <span className="text-xs text-gray-600">Want to repeat the last activity?</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyLastRecord}
                className="text-xs h-7 gap-1 border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <Copy className="w-3 h-3" /> Auto-Fill Last Record
              </Button>
            </div>
          ) : null}

          <form onSubmit={handleAdd} className="space-y-4 pt-1">
            {/* Student Select */}
            <div>
              <Label className="text-xs font-bold text-gray-700 mb-1 block">Select Student *</Label>
              <select
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={formData.student_id}
                onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                required
              >
                <option value="">Select a student...</option>
                {students.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.readable_id || s.class_name || "Student"})
                  </option>
                ))}
              </select>
            </div>

            {/* Date & Activity Type Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-700 mb-1 block">Date *</Label>
                <Input
                  type="date"
                  max={today}
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="text-xs"
                  required
                />
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700 mb-1 block">Activity Type *</Label>
                <select
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={formData.activity_type}
                  onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
                  required
                >
                  <option value="sports">Sports</option>
                  <option value="cultural">Cultural</option>
                  <option value="academic">Academic</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Activity Name */}
            <div>
              <Label className="text-xs font-bold text-gray-700 mb-1 block">Activity Name *</Label>
              <Input
                value={formData.activity_name}
                onChange={(e) => setFormData({ ...formData, activity_name: e.target.value })}
                placeholder="e.g., Football Match, Debate Competition, Science Fair"
                className="text-xs"
                required
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs font-bold text-gray-700 mb-1 block">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Details of student's participation or achievement..."
                className="text-xs resize-none h-20"
              />
            </div>

            {/* Remarks */}
            <div>
              <Label className="text-xs font-bold text-gray-700 mb-1 block">Remarks / Notes</Label>
              <Input
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                placeholder="Optional warden notes or remarks"
                className="text-xs"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAddDialog(false);
                  setCopiedFromInfo(null);
                }}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                size="sm"
                className="btn-primary text-white text-xs gap-1.5"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-3.5 h-3.5" />
                    {copiedFromInfo ? "Save Copied Activity" : "Add Activity"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------- */}
      {/* CONFIRM OVERWRITE DIALOG */}
      {/* ------------------------------------------------------------- */}
      <Dialog
        open={showConfirmDialog}
        onOpenChange={(open) => {
          setShowConfirmDialog(open);
          if (!open) setDuplicateInfo(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-gray-900">
              Overwrite Existing Activity?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-xs">
            <p className="text-gray-600">{duplicateInfo?.message}</p>
            {duplicateInfo?.existing && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
                <p className="font-bold text-amber-900">Existing Record Details:</p>
                <p><strong>Type:</strong> {duplicateInfo.existing.activity_type}</p>
                <p><strong>Name:</strong> {duplicateInfo.existing.activity_name}</p>
                {duplicateInfo.existing.description && (
                  <p><strong>Description:</strong> {duplicateInfo.existing.description}</p>
                )}
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowConfirmDialog(false);
                  setDuplicateInfo(null);
                }}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmOverwrite}
                size="sm"
                className="btn-primary text-white text-xs"
              >
                Yes, Overwrite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------- */}
      {/* BULK ADD ACTIVITY DIALOG (DATE RANGE & ASSIGNED STUDENTS) */}
      {/* ------------------------------------------------------------- */}
      <Dialog open={showBulkAddDialog} onOpenChange={setShowBulkAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-5 pb-3 border-b border-gray-100">
            <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              <span>Bulk Add Activity</span>
            </DialogTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Record activities across a date range for your assigned students
            </p>
          </DialogHeader>

          <form onSubmit={handleBulkAddSubmit} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* STEP 1: DATE RANGE */}
              <div className="space-y-2.5 bg-gray-50/70 p-3.5 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Label className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                    <span>1. Select Date Range *</span>
                  </Label>
                  {/* Quick Shortcuts */}
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleDateShortcut("today")}
                      className="px-2 py-0.5 rounded text-[11px] font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDateShortcut("yesterday")}
                      className="px-2 py-0.5 rounded text-[11px] font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                    >
                      Yesterday
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDateShortcut("last3days")}
                      className="px-2 py-0.5 rounded text-[11px] font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                    >
                      Last 3 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDateShortcut("last7days")}
                      className="px-2 py-0.5 rounded text-[11px] font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                    >
                      Last 7 Days
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div>
                    <Label className="text-[11px] text-gray-500 mb-1 block">Start Date *</Label>
                    <Input
                      type="date"
                      max={today}
                      value={bulkAddStartDate}
                      onChange={(e) => setBulkAddStartDate(e.target.value)}
                      className="text-xs bg-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-gray-500 mb-1 block">End Date *</Label>
                    <Input
                      type="date"
                      max={today}
                      min={bulkAddStartDate || ""}
                      value={bulkAddEndDate}
                      onChange={(e) => setBulkAddEndDate(e.target.value)}
                      className="text-xs bg-white"
                      required
                    />
                  </div>
                </div>

                {bulkAddStartDate && bulkAddEndDate && (
                  <div className="text-[11px] text-indigo-700 bg-indigo-50/70 border border-indigo-100 p-2 rounded-lg flex items-center gap-1.5 font-medium">
                    <Calendar className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                    <span>
                      Span: <strong>{bulkDaysCount} day{bulkDaysCount > 1 ? "s" : ""}</strong> ({bulkAddStartDate} to {bulkAddEndDate})
                    </span>
                  </div>
                )}
              </div>

              {/* STEP 2: SELECT ASSIGNED STUDENTS */}
              <div className="space-y-2.5 bg-gray-50/70 p-3.5 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <Label className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-indigo-600" />
                      <span>2. Select Assigned Students *</span>
                    </Label>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Showing only students assigned to your warden account ({students.length} total)
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAllStudents}
                      className="h-6 text-[11px] px-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    >
                      Select All
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDeselectAllStudents}
                      className="h-6 text-[11px] px-2 text-gray-600 hover:bg-gray-100"
                    >
                      Clear
                    </Button>
                    <Badge variant="secondary" className="text-[11px] bg-indigo-100 text-indigo-800 border-indigo-200">
                      {selectedStudentIds.length} Selected
                    </Badge>
                  </div>
                </div>

                {/* Search & Filter Bar */}
                <div className="flex items-center gap-2 pt-1">
                  <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Filter assigned students by name, ID, class, room..."
                      value={bulkStudentSearch}
                      onChange={(e) => setBulkStudentSearch(e.target.value)}
                      className="h-8 pl-8 text-xs bg-white"
                    />
                  </div>
                  {availableClasses.length > 1 && (
                    <div className="w-36">
                      <select
                        value={bulkStudentClassFilter}
                        onChange={(e) => setBulkStudentClassFilter(e.target.value)}
                        className="w-full h-8 px-2 bg-white border border-gray-200 rounded-md text-xs font-medium focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                      >
                        <option value="all">All Classes</option>
                        {availableClasses.map(c => (
                          <option key={c} value={c}>Class {c}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {/* Selected Students Chips Preview */}
                {selectedStudentIds.length > 0 && (
                  <div className="flex flex-wrap gap-1 p-2 bg-white rounded-lg border border-indigo-100 max-h-20 overflow-y-auto">
                    {selectedStudentIds.map(id => {
                      const stu = students.find(s => s.id === id);
                      if (!stu) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-200"
                        >
                          <span>{stu.name}</span>
                          <button
                            type="button"
                            onClick={() => handleToggleStudent(id)}
                            className="text-indigo-400 hover:text-indigo-800 ml-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Student Checklist Container */}
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100 bg-white">
                  {filteredAssignedStudents.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-500">
                      No assigned students match your filter.
                    </div>
                  ) : (
                    filteredAssignedStudents.map(student => {
                      const isSelected = selectedStudentIds.includes(student.id);
                      return (
                        <div
                          key={student.id}
                          onClick={() => handleToggleStudent(student.id)}
                          className={`flex items-center justify-between p-2.5 text-xs cursor-pointer transition-colors ${
                            isSelected ? "bg-indigo-50/50 hover:bg-indigo-50" : "hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                            />
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{student.name}</p>
                              <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                                {student.readable_id && (
                                  <span className="font-mono bg-gray-100 px-1 rounded text-gray-600">
                                    {student.readable_id}
                                  </span>
                                )}
                                {student.class_name && <span>• Class {student.class_name}</span>}
                                {student.room_number && <span>• Room {student.room_number}</span>}
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <CheckCircle className="w-4 h-4 text-indigo-600 shrink-0" />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* STEP 3: ACTIVITY DETAILS */}
              <div className="space-y-3.5 bg-gray-50/70 p-3.5 rounded-xl border border-gray-200">
                <Label className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                  <span>3. Activity Information *</span>
                </Label>

                {/* Activity Type Selection Pills */}
                <div>
                  <Label className="text-[11px] text-gray-500 mb-1.5 block">Activity Type *</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {["sports", "cultural", "academic", "other"].map(type => {
                      const cfg = getActivityTypeConfig(type);
                      const isSelected = bulkAddFormData.activity_type === type;
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setBulkAddFormData(prev => ({ ...prev, activity_type: type }))}
                          className={`p-2 rounded-xl border text-xs font-semibold flex items-center justify-center gap-1.5 transition-all ${
                            isSelected
                              ? "border-indigo-500 bg-indigo-50 text-indigo-900 shadow-2xs ring-1 ring-indigo-500"
                              : "border-gray-200 hover:border-gray-300 text-gray-700 bg-white"
                          }`}
                        >
                          {cfg.icon}
                          <span>{cfg.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Activity Name */}
                <div>
                  <Label className="text-[11px] text-gray-500 mb-1 block">Activity Name *</Label>
                  <Input
                    value={bulkAddFormData.activity_name}
                    onChange={(e) => setBulkAddFormData(prev => ({ ...prev, activity_name: e.target.value }))}
                    placeholder="e.g., Morning PT, Inter-House Football, Science Project, Creative Writing"
                    className="text-xs bg-white"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <Label className="text-[11px] text-gray-500 mb-1 block">Description (Optional)</Label>
                  <Textarea
                    value={bulkAddFormData.description}
                    onChange={(e) => setBulkAddFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Details of participation, goals achieved, performance..."
                    className="text-xs resize-none h-16 bg-white"
                  />
                </div>

                {/* Remarks */}
                <div>
                  <Label className="text-[11px] text-gray-500 mb-1 block">Remarks / Notes (Optional)</Label>
                  <Input
                    value={bulkAddFormData.remarks}
                    onChange={(e) => setBulkAddFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="e.g., Exceptional teamwork, Full attendance, Coach commended"
                    className="text-xs bg-white"
                  />
                </div>

                {/* Overwrite Checkbox */}
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="bulk-overwrite-checkbox"
                    checked={bulkAddFormData.overwrite_existing}
                    onChange={(e) => setBulkAddFormData(prev => ({ ...prev, overwrite_existing: e.target.checked }))}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                  />
                  <label htmlFor="bulk-overwrite-checkbox" className="text-xs text-gray-700 cursor-pointer">
                    Overwrite existing records if same activity name already exists on that date
                  </label>
                </div>
              </div>

              {/* LIVE SUMMARY BANNER */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-3.5 text-xs text-indigo-950 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-600 shrink-0" />
                  <span>
                    Summary: <strong>{selectedStudentIds.length} student{selectedStudentIds.length === 1 ? "" : "s"}</strong> × <strong>{bulkDaysCount} day{bulkDaysCount === 1 ? "" : "s"}</strong> = <strong>{selectedStudentIds.length * bulkDaysCount} total records</strong>
                  </span>
                </div>
              </div>
            </div>

            {/* STICKY FOOTER */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowBulkAddDialog(false)}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={bulkAddLoading || selectedStudentIds.length === 0 || !bulkAddFormData.activity_name.trim()}
                size="sm"
                className="btn-primary text-white text-xs gap-1.5"
              >
                {bulkAddLoading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Recording Activities...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-3.5 h-3.5" />
                    Record Activity for {selectedStudentIds.length} Student{selectedStudentIds.length === 1 ? "" : "s"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------- */}
      {/* EDIT ACTIVITY DIALOG */}
      {/* ------------------------------------------------------------- */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Edit className="w-5 h-5 text-amber-600" />
              <span>Edit Activity</span>
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="space-y-4 pt-1">
            {/* Student Info / Reassign */}
            <div>
              <Label className="text-xs font-bold text-gray-700 mb-1 block">Student *</Label>
              <select
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                value={editFormData.student_id}
                onChange={(e) => setEditFormData({ ...editFormData, student_id: e.target.value })}
                required
              >
                <option value="">Select student...</option>
                {students.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.readable_id || s.class_name || "Student"})
                  </option>
                ))}
              </select>
            </div>

            {/* Date & Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-700 mb-1 block">Date *</Label>
                <Input
                  type="date"
                  max={today}
                  value={editFormData.date}
                  onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                  className="text-xs"
                  required
                />
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700 mb-1 block">Activity Type *</Label>
                <select
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={editFormData.activity_type}
                  onChange={(e) => setEditFormData({ ...editFormData, activity_type: e.target.value })}
                  required
                >
                  <option value="sports">Sports</option>
                  <option value="cultural">Cultural</option>
                  <option value="academic">Academic</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Activity Name */}
            <div>
              <Label className="text-xs font-bold text-gray-700 mb-1 block">Activity Name *</Label>
              <Input
                value={editFormData.activity_name}
                onChange={(e) => setEditFormData({ ...editFormData, activity_name: e.target.value })}
                placeholder="Activity name..."
                className="text-xs"
                required
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-xs font-bold text-gray-700 mb-1 block">Description</Label>
              <Textarea
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                placeholder="Details of student's participation..."
                className="text-xs resize-none h-20"
              />
            </div>

            {/* Remarks */}
            <div>
              <Label className="text-xs font-bold text-gray-700 mb-1 block">Remarks / Notes</Label>
              <Input
                value={editFormData.remarks}
                onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
                placeholder="Optional notes or remarks"
                className="text-xs"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowEditDialog(false)}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={editLoading}
                size="sm"
                className="btn-primary text-white text-xs gap-1.5"
              >
                {editLoading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Saving Changes...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-3.5 h-3.5" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------- */}
      {/* DELETE ACTIVITY CONFIRMATION DIALOG */}
      {/* ------------------------------------------------------------- */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              <span>Delete Activity Record?</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3.5 text-xs pt-1">
            <p className="text-gray-600">
              Are you sure you want to permanently delete this activity record? This action cannot be undone.
            </p>

            {activityToDelete && (
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-3.5 space-y-1.5 text-rose-950">
                <p className="font-bold text-sm text-rose-900">{activityToDelete.activity_name}</p>
                <div className="text-[11px] text-rose-800 space-y-0.5">
                  <p><strong>Student:</strong> {activityToDelete.student_name} ({activityToDelete.readable_id || "N/A"})</p>
                  <p><strong>Date:</strong> {activityToDelete.date}</p>
                  <p><strong>Type:</strong> {activityToDelete.activity_type}</p>
                  {activityToDelete.description && (
                    <p><strong>Description:</strong> {activityToDelete.description}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setActivityToDelete(null);
                }}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white text-xs gap-1.5"
              >
                {deleteLoading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete Activity
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Activities;